"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isValidCSSColor = isValidCSSColor;
exports.validateGradientStyle = validateGradientStyle;
exports.validateButtonColor = validateButtonColor;
exports.sanitizeButtonColor = sanitizeButtonColor;
exports.validateCustomStyle = validateCustomStyle;
exports.sanitizeGradientStyle = sanitizeGradientStyle;
exports.parseStyleText = parseStyleText;
exports.styleValidationToParseErrors = styleValidationToParseErrors;
/**
 * Check if a string is a valid CSS color
 * Supports: hex (#RGB, #RRGGBB, #RRGGBBAA), rgb/rgba, hsl/hsla, and named colors
 */
function isValidCSSColor(color) {
    if (!color || typeof color !== 'string') {
        return false;
    }
    const trimmedColor = color.trim();
    // Check for hex colors
    const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
    if (hexPattern.test(trimmedColor)) {
        return true;
    }
    // Check for rgb/rgba
    const rgbPattern = /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/;
    if (rgbPattern.test(trimmedColor)) {
        return true;
    }
    // Check for hsl/hsla
    const hslPattern = /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*(,\s*[\d.]+\s*)?\)$/;
    if (hslPattern.test(trimmedColor)) {
        return true;
    }
    // Check for named colors
    const namedColors = [
        'aliceblue', 'antiquewhite', 'aqua', 'aquamarine', 'azure',
        'beige', 'bisque', 'black', 'blanchedalmond', 'blue', 'blueviolet', 'brown', 'burlywood',
        'cadetblue', 'chartreuse', 'chocolate', 'coral', 'cornflowerblue', 'cornsilk', 'crimson', 'cyan',
        'darkblue', 'darkcyan', 'darkgoldenrod', 'darkgray', 'darkgreen', 'darkgrey', 'darkkhaki',
        'darkmagenta', 'darkolivegreen', 'darkorange', 'darkorchid', 'darkred', 'darksalmon',
        'darkseagreen', 'darkslateblue', 'darkslategray', 'darkslategrey', 'darkturquoise', 'darkviolet',
        'deeppink', 'deepskyblue', 'dimgray', 'dimgrey', 'dodgerblue',
        'firebrick', 'floralwhite', 'forestgreen', 'fuchsia',
        'gainsboro', 'ghostwhite', 'gold', 'goldenrod', 'gray', 'green', 'greenyellow', 'grey',
        'honeydew', 'hotpink', 'indianred', 'indigo', 'ivory',
        'khaki', 'lavender', 'lavenderblush', 'lawngreen', 'lemonchiffon', 'lightblue', 'lightcoral',
        'lightcyan', 'lightgoldenrodyellow', 'lightgray', 'lightgreen', 'lightgrey', 'lightpink',
        'lightsalmon', 'lightseagreen', 'lightskyblue', 'lightslategray', 'lightslategrey',
        'lightsteelblue', 'lightyellow', 'lime', 'limegreen', 'linen',
        'magenta', 'maroon', 'mediumaquamarine', 'mediumblue', 'mediumorchid', 'mediumpurple',
        'mediumseagreen', 'mediumslateblue', 'mediumspringgreen', 'mediumturquoise', 'mediumvioletred',
        'midnightblue', 'mintcream', 'mistyrose', 'moccasin',
        'navajowhite', 'navy', 'oldlace', 'olive', 'olivedrab', 'orange', 'orangered', 'orchid',
        'palegoldenrod', 'palegreen', 'paleturquoise', 'palevioletred', 'papayawhip', 'peachpuff',
        'peru', 'pink', 'plum', 'powderblue', 'purple',
        'rebeccapurple', 'red', 'rosybrown', 'royalblue',
        'saddlebrown', 'salmon', 'sandybrown', 'seagreen', 'seashell', 'sienna', 'silver', 'skyblue',
        'slateblue', 'slategray', 'slategrey', 'snow', 'springgreen', 'steelblue',
        'tan', 'teal', 'thistle', 'tomato', 'turquoise',
        'violet', 'wheat', 'white', 'whitesmoke', 'yellow', 'yellowgreen'
    ];
    return namedColors.includes(trimmedColor.toLowerCase());
}
/**
 * Validate a gradient style object
 */
function validateGradientStyle(gradient, context = 'gradient') {
    const errors = [];
    const warnings = [];
    // Validate colors array
    if (!gradient.colors || !Array.isArray(gradient.colors)) {
        errors.push(`${context}: colors must be an array`);
        return { isValid: false, errors, warnings };
    }
    if (gradient.colors.length < 2) {
        errors.push(`${context}: at least 2 colors required (got ${gradient.colors.length})`);
    }
    if (gradient.colors.length > 5) {
        warnings.push(`${context}: more than 5 colors may reduce visual clarity (got ${gradient.colors.length})`);
    }
    // Validate each color
    const validColors = [];
    gradient.colors.forEach((color, index) => {
        if (!isValidCSSColor(color)) {
            errors.push(`${context}: invalid color at index ${index}: "${color}"`);
        }
        else {
            validColors.push(color.trim());
        }
    });
    // Validate angle
    if (gradient.angle !== undefined) {
        if (typeof gradient.angle !== 'number') {
            errors.push(`${context}: angle must be a number`);
        }
        else if (gradient.angle < 0 || gradient.angle > 360) {
            errors.push(`${context}: angle must be between 0 and 360 degrees (got ${gradient.angle})`);
        }
    }
    const isValid = errors.length === 0 && validColors.length >= 2;
    // Create sanitized style if valid
    let sanitizedStyle;
    if (isValid) {
        sanitizedStyle = {
            backgroundGradient: {
                colors: validColors,
                angle: gradient.angle ?? 135
            }
        };
    }
    return {
        isValid,
        errors,
        warnings,
        sanitizedStyle
    };
}
/**
 * Validate button color
 */
function validateButtonColor(color, context = 'button_color') {
    const errors = [];
    const warnings = [];
    if (!isValidCSSColor(color)) {
        errors.push(`${context}: invalid button color: "${color}". Must be a valid CSS color (hex, rgb, hsl, or named color).`);
        return { isValid: false, errors, warnings };
    }
    return {
        isValid: true,
        errors,
        warnings,
        sanitizedStyle: {
            buttonColor: color.trim()
        }
    };
}
/**
 * Sanitize button color by trimming and validating
 * Returns undefined if invalid
 */
function sanitizeButtonColor(color) {
    if (!color || typeof color !== 'string' || !isValidCSSColor(color)) {
        return undefined;
    }
    return color.trim();
}
/**
 * Validate a complete custom style object
 */
function validateCustomStyle(style, context = 'style') {
    const errors = [];
    const warnings = [];
    if (!style || typeof style !== 'object') {
        errors.push(`${context}: style must be an object`);
        return { isValid: false, errors, warnings };
    }
    const sanitizedStyle = {};
    let hasAnyValidStyle = false;
    // Validate background gradient if present
    if (style.backgroundGradient) {
        const gradientResult = validateGradientStyle(style.backgroundGradient, `${context}.backgroundGradient`);
        errors.push(...gradientResult.errors);
        warnings.push(...gradientResult.warnings);
        if (gradientResult.isValid && gradientResult.sanitizedStyle?.backgroundGradient) {
            sanitizedStyle.backgroundGradient = gradientResult.sanitizedStyle.backgroundGradient;
            hasAnyValidStyle = true;
        }
    }
    // Validate button color if present
    if (style.buttonColor) {
        const buttonColorResult = validateButtonColor(style.buttonColor, `${context}.buttonColor`);
        errors.push(...buttonColorResult.errors);
        warnings.push(...buttonColorResult.warnings);
        if (buttonColorResult.isValid && buttonColorResult.sanitizedStyle?.buttonColor) {
            sanitizedStyle.buttonColor = buttonColorResult.sanitizedStyle.buttonColor;
            hasAnyValidStyle = true;
        }
    }
    // If no valid styles, check if it's completely empty
    if (!hasAnyValidStyle) {
        if (!style.backgroundGradient && !style.buttonColor) {
            warnings.push(`${context}: no style properties defined`);
        }
        // Return invalid if there were errors but no valid styles
        if (errors.length > 0) {
            return { isValid: false, errors, warnings };
        }
    }
    return {
        isValid: hasAnyValidStyle || errors.length === 0,
        errors,
        warnings,
        sanitizedStyle: hasAnyValidStyle ? sanitizedStyle : undefined
    };
}
/**
 * Sanitize a gradient style by removing invalid colors and applying defaults
 * Returns null if the gradient cannot be sanitized
 */
function sanitizeGradientStyle(gradient) {
    const validColors = gradient.colors.filter(color => isValidCSSColor(color)).map(c => c.trim());
    if (validColors.length < 2) {
        return null; // Cannot sanitize - need at least 2 valid colors
    }
    return {
        colors: validColors,
        angle: gradient.angle !== undefined &&
            typeof gradient.angle === 'number' &&
            gradient.angle >= 0 &&
            gradient.angle <= 360
            ? gradient.angle
            : 135 // Default angle
    };
}
/**
 * Parse style properties from text (as returned by tokenizer)
 * Format:
 *   background_gradient: #color1, #color2, #color3
 *   gradient_angle: 135
 *   button_color: #color
 */
function parseStyleText(styleText) {
    if (!styleText || typeof styleText !== 'string') {
        return null;
    }
    const lines = styleText.split('\n').map(line => line.trim()).filter(line => line);
    let colors;
    let angle;
    let buttonColor;
    for (const line of lines) {
        const parts = line.split(':');
        if (parts.length < 2) {
            continue;
        }
        const key = parts[0].trim().toLowerCase();
        const value = parts.slice(1).join(':').trim();
        if (key === 'background_gradient') {
            // Parse comma-separated colors
            colors = value.split(',').map(c => c.trim()).filter(c => c);
        }
        else if (key === 'gradient_angle') {
            const parsedAngle = parseInt(value, 10);
            if (!isNaN(parsedAngle)) {
                angle = parsedAngle;
            }
        }
        else if (key === 'button_color') {
            buttonColor = value;
        }
    }
    // Build style object only if we have at least one property
    const style = {};
    let hasAnyProperty = false;
    if (colors && colors.length > 0) {
        style.backgroundGradient = {
            colors,
            angle
        };
        hasAnyProperty = true;
    }
    if (buttonColor) {
        style.buttonColor = buttonColor;
        hasAnyProperty = true;
    }
    return hasAnyProperty ? style : null;
}
/**
 * Convert validation errors to ParseError format
 */
function styleValidationToParseErrors(result, line) {
    const parseErrors = [];
    result.errors.forEach(error => {
        parseErrors.push({
            line,
            message: error,
            type: 'validation'
        });
    });
    result.warnings.forEach(warning => {
        parseErrors.push({
            line,
            message: warning,
            type: 'warning'
        });
    });
    return parseErrors;
}
//# sourceMappingURL=styleValidation.js.map